import type { SystemRole } from './rail-rbac';

export type IconName =
  | 'home'
  | 'book'
  | 'inbox'
  | 'calendar'
  | 'users'
  | 'ticket'
  | 'file-text'
  | 'sparkles'
  | 'syringe'
  | 'package'
  | 'wallet'
  | 'user-cog'
  | 'bar-chart'
  | 'activity'
  | 'check-square'
  | 'clipboard-check'
  | 'settings';

export type BadgeQueryRef =
  | {
      kind: 'sql';
      rpc: string;
      realtime?: { table: string; filter?: string };
    }
  | { kind: 'function'; fn: () => Promise<number> };

export interface RailSubPage {
  id: string;
  label: string;
  href: string;
  /** Browser-tab title when it should differ from the rail label: the tab
   *  names the page by its own heading (pageTitle). */
  tabTitle?: string;
  minRole: SystemRole;
  badgeQuery?: BadgeQueryRef;
  /**
   * RAIL-IA amendment A1: render the badge in the `--error` family
   * instead of the default accent pill, and propagate error tone up to
   * the module aggregate + mobile tab dot. Used by Inbox → Urgencias
   * (post-procedure complications — amber undersells it).
   */
  badgeTone?: 'error';
  /**
   * Reviewer-only sub-page (superadmin OR clinical role MD). "MD" is not a
   * system_role tier, so getVisibleRail applies isComfyReviewer on top of the
   * minRole check. Used by IA → Revisiones (the Comfy accuracy-review queue).
   */
  requiresReviewer?: boolean;
  defaultPriority?: number;
}

export interface RailQuickAdd {
  label: string;
  shortcut?: string;
  panelComponent: string;
  expandHref?: string;
  minRole: SystemRole;
}

/**
 * RAIL-IA amendment A5: `mobileTabPriority` is per-role. A plain number
 * applies to every role (resolver falls back to it); a partial record
 * gives role-specific priorities. `getMobileTabs` resolves the caller's
 * role, so a module can be a bottom tab for some roles and not others.
 */
export type MobileTabPriority = number | Partial<Record<SystemRole, number>>;

export interface RailModule {
  id: string;
  label: string;
  href: string;
  icon: IconName;
  minRole: SystemRole;
  mobileTabPriority?: MobileTabPriority;
  quickAdd?: RailQuickAdd;
  subPages: RailSubPage[];
}

/**
 * Sidebar visual grouping. Inicio sits alone on top (no eyebrow); the
 * daily-clinical tools and the back-office modules form two labeled
 * groups. The rail renders modules in this group order and skips any
 * group left empty after role gating. Pure presentation — it does not
 * affect routing, role gates, or RAIL_CONFIG itself.
 */
export const RAIL_GROUPS: { id: string; label: string | null; modules: string[] }[] = [
  { id: 'principal', label: null, modules: ['home', 'ai'] },
  { id: 'dia', label: 'Día a día', modules: ['inbox', 'citas', 'pacientes', 'tickets', 'notas', 'tareas'] },
  {
    id: 'gestion',
    label: 'Gestión',
    modules: ['tratamientos', 'inventario', 'gastos', 'staff', 'verificaciones', 'pulso', 'reportes', 'configuracion'],
  },
];

/**
 * RAIL_CONFIG — single source of truth for the rail UI, role gates,
 * route guards, badge queries, and quick-add metadata.
 *
 * Reconciled against the approved rail spec (docs/superpowers/specs/
 * 2026-05-25-rehaut-rail-subnav-system-design.md §4.1/§4.3), the five
 * RAIL-IA amendments (10 jun), and the rehaut-shell kit's data.js (the
 * designated fixture). Module display order follows the kit.
 *
 * Sub-page ids/hrefs use the production kebab-case route grain (spec
 * §6.1), not the kit's terse mock ids; the kit header notes production
 * is authoritative for ids/hrefs.
 *
 * Cardinality (verified by rail.test): staff 27 sub-pages / 11 modules,
 * admin 34 / 13, superadmin 58 / 16 (incl. Asistente + Pulso, superadmin-only).
 */
export const RAIL_CONFIG: RailModule[] = [
  // ── Home ──────────────────────────────────────────────────────
  {
    id: 'home',
    label: 'Inicio',
    href: '/home',
    icon: 'home',
    minRole: 'staff',
    mobileTabPriority: 100, // uniform across roles (1st tab everywhere)
    subPages: [
      { id: 'hoy',    label: 'Hoy',           href: '/home/hoy',    minRole: 'staff', defaultPriority: 100 },
      // tabTitle: the page's heading is "Agenda de hoy"; the tab said
      // "Agenda 3 días" (round-10 iPad audit).
      { id: 'agenda', label: 'Agenda 3 días', tabTitle: 'Agenda de hoy', href: '/home/agenda', minRole: 'staff', defaultPriority: 50 },
    ],
  },

  // ── Inbox (A1: added to the IA — was missing entirely) ────────
  {
    id: 'inbox',
    // P94: matches the page title ("La bandeja"); route stays /inbox.
    label: 'Bandeja',
    href: '/inbox',
    icon: 'inbox',
    minRole: 'staff',
    mobileTabPriority: { staff: 95, superadmin: 70 }, // staff 2nd, super 3rd; not an admin tab
    subPages: [
      {
        id: 'todos',
        label: 'Todos',
        href: '/inbox/todos',
        minRole: 'staff',
        defaultPriority: 100,
        badgeQuery: { kind: 'sql', rpc: 'inbox_actionable_count', realtime: { table: 'rasayel_conversation_intents' } },
      },
      {
        id: 'urgencias',
        label: 'Urgencias',
        href: '/inbox/urgencias',
        minRole: 'staff',
        defaultPriority: 90,
        badgeQuery: { kind: 'sql', rpc: 'inbox_urgencias_count', realtime: { table: 'rasayel_conversation_intents' } },
        badgeTone: 'error', // post-procedure complications
      },
      { id: 'pospuestas',    label: 'Pospuestas',    href: '/inbox/pospuestas',    minRole: 'staff', defaultPriority: 60 },
      { id: 'resueltas',     label: 'Resueltas hoy', href: '/inbox/resueltas',     minRole: 'staff', defaultPriority: 40 },
      { id: 'automatizados', label: 'Automatizados', href: '/inbox/automatizados', minRole: 'staff', defaultPriority: 20 },
      { id: 'campanas',      label: 'Campañas',      href: '/inbox/campanas',      minRole: 'superadmin', defaultPriority: 10 },
    ],
  },

  // ── Citas ─────────────────────────────────────────────────────
  {
    id: 'citas',
    label: 'Citas',
    href: '/citas',
    icon: 'calendar',
    minRole: 'staff',
    mobileTabPriority: { staff: 90, admin: 95, superadmin: 95 },
    quickAdd: {
      label: '+ Nueva cita',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewCitaPanel',
      expandHref: '/citas/new',
      minRole: 'staff',
    },
    subPages: [
      { id: 'hoy',         label: 'Hoy',         href: '/citas/hoy',         minRole: 'staff', defaultPriority: 100 },
      { id: 'esta-semana', label: 'Esta semana', href: '/citas/esta-semana', minRole: 'staff', defaultPriority: 80 },
      {
        id: 'por-confirmar',
        label: 'Por confirmar',
        href: '/citas/por-confirmar',
        minRole: 'staff',
        defaultPriority: 60,
        badgeQuery: { kind: 'sql', rpc: 'count_citas_por_confirmar', realtime: { table: 'citas', filter: 'status=eq.por_confirmar' } },
      },
      { id: 'analisis', label: 'Análisis', href: '/citas/analisis', minRole: 'admin', defaultPriority: 10 },
    ],
  },

  // ── Pacientes ─────────────────────────────────────────────────
  {
    id: 'pacientes',
    label: 'Pacientes',
    href: '/pacientes',
    icon: 'users',
    minRole: 'staff',
    quickAdd: {
      label: '+ Nuevo paciente',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewPacientePanel',
      expandHref: '/pacientes/new',
      minRole: 'staff',
    },
    subPages: [
      { id: 'activos',   label: 'Activos',   href: '/pacientes/activos',   minRole: 'staff', defaultPriority: 100 },
      { id: 'recientes', label: 'Recientes', href: '/pacientes/recientes', minRole: 'staff', defaultPriority: 70 },
      {
        id: 'incompletos',
        label: 'Expedientes incompletos',
        href: '/pacientes/incompletos',
        minRole: 'staff',
        defaultPriority: 40,
        badgeQuery: { kind: 'sql', rpc: 'count_expedientes_incompletos', realtime: { table: 'pacientes', filter: 'expediente_complete=eq.false' } },
      },
      {
        id: 'segmentos',
        label: 'Segmentos',
        href: '/pacientes/segmentos',
        minRole: 'superadmin',
        defaultPriority: 20,
      },
    ],
  },

  // ── Tickets (Historial folded into the cockpit — no subpage) ──
  {
    id: 'tickets',
    // P94: matches the page title ("Cobro"); route stays /tickets.
    label: 'Cobro',
    href: '/tickets',
    icon: 'ticket',
    minRole: 'staff',
    mobileTabPriority: { staff: 85, admin: 90 }, // staff 4th, admin 3rd; not a super tab
    quickAdd: {
      label: '+ Nuevo ticket',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewTicketPanel',
      expandHref: '/tickets/new',
      minRole: 'staff',
    },
    subPages: [
      {
        id: 'activos',
        label: 'Activos',
        href: '/tickets/activos',
        minRole: 'staff',
        defaultPriority: 100,
        badgeQuery: { kind: 'sql', rpc: 'count_tickets_activos', realtime: { table: 'tickets', filter: 'status=eq.activo' } },
      },
      { id: 'cerrados-hoy', label: 'Cerrados hoy', href: '/tickets/cerrados-hoy', minRole: 'staff', defaultPriority: 70 },
      // Historial folded into the /tickets cockpit's "Todos / Historial"
      // segment (superadmin) + "Últimos 30 días" (admin) — no separate route.
    ],
  },

  // ── Notas ─────────────────────────────────────────────────────
  {
    id: 'notas',
    label: 'Notas',
    href: '/notas',
    icon: 'file-text',
    minRole: 'staff',
    quickAdd: {
      label: '+ Nueva nota',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewNotaPanel',
      expandHref: '/notas/new',
      minRole: 'staff',
    },
    subPages: [
      {
        id: 'pendientes',
        label: 'Pendientes',
        href: '/notas/pendientes',
        minRole: 'staff',
        defaultPriority: 100,
        badgeQuery: { kind: 'sql', rpc: 'count_notas_pendientes', realtime: { table: 'notas_medicas', filter: 'status=eq.pendiente' } },
      },
      { id: 'por-paciente', label: 'Por paciente', href: '/notas/por-paciente', minRole: 'staff', defaultPriority: 60 },
      { id: 'completadas',  label: 'Completadas',  href: '/notas/completadas',  minRole: 'staff', defaultPriority: 30 },
    ],
  },

  // ── Tareas (team task board) ──────────────────────────────────
  {
    id: 'tareas',
    label: 'Tareas',
    href: '/tareas',
    icon: 'check-square',
    minRole: 'staff',
    subPages: [
      { id: 'asignadas', label: 'Asignadas a mí', href: '/tareas/asignadas', minRole: 'staff', defaultPriority: 100 },
      { id: 'tableros', label: 'Tableros', href: '/tareas/tableros', minRole: 'staff', defaultPriority: 80 },
    ],
  },

  // ── Tratamientos (D3: quick-add admin-min) ────────────────────
  {
    id: 'tratamientos',
    label: 'Tratamientos',
    href: '/tratamientos',
    // Own glyph: the sparkle is IA's. On a touch screen the collapsed rail
    // has no hover labels, so two modules sharing an icon read as one (iL30).
    icon: 'syringe',
    minRole: 'staff',
    quickAdd: {
      label: '+ Nuevo',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewTratamientoPanel',
      expandHref: '/tratamientos/new',
      minRole: 'admin',
    },
    subPages: [
      { id: 'catalogo',   label: 'Catálogo',   href: '/tratamientos/catalogo',   minRole: 'staff', defaultPriority: 100 },
      { id: 'bundles',    label: 'Bundles',    href: '/tratamientos/bundles',    minRole: 'staff', defaultPriority: 70 },
      { id: 'categorias', label: 'Categorías', href: '/tratamientos/categorias', minRole: 'staff', defaultPriority: 40 },
    ],
  },

  // ── Inventario (D4: quick-add staff-min) ──────────────────────
  {
    id: 'inventario',
    label: 'Inventario',
    href: '/inventario',
    icon: 'package',
    minRole: 'staff',
    quickAdd: {
      label: '+ Movimiento',
      shortcut: 'M',
      panelComponent: '@/components/quickadd/NewMovimientoPanel',
      expandHref: '/inventario/new',
      minRole: 'staff',
    },
    subPages: [
      { id: 'stock',       label: 'Stock',       href: '/inventario/stock',       minRole: 'staff', defaultPriority: 100 },
      { id: 'movimientos', label: 'Movimientos', href: '/inventario/movimientos', minRole: 'staff', defaultPriority: 70 },
      {
        id: 'alertas',
        label: 'Alertas bajas',
        href: '/inventario/alertas',
        minRole: 'staff',
        defaultPriority: 40,
        badgeQuery: { kind: 'sql', rpc: 'count_inventario_alertas', realtime: { table: 'inventario_items', filter: 'stock=lte.low_threshold' } },
      },
    ],
  },

  // ── Gastos (admin-min) ────────────────────────────────────────
  {
    id: 'gastos',
    label: 'Gastos',
    href: '/gastos',
    icon: 'wallet',
    minRole: 'admin',
    mobileTabPriority: { admin: 85 }, // admin 4th tab
    quickAdd: {
      label: '+ Nuevo gasto',
      shortcut: 'N',
      panelComponent: '@/components/quickadd/NewGastoPanel',
      expandHref: '/gastos/new',
      minRole: 'admin',
    },
    subPages: [
      { id: 'activos',       label: 'Activos',       href: '/gastos/activos',       minRole: 'admin', defaultPriority: 100 },
      { id: 'por-categoria', label: 'Por categoría', href: '/gastos/por-categoria', minRole: 'admin', defaultPriority: 70 },
      { id: 'vendors',       label: 'Vendors',       href: '/gastos/vendors',       minRole: 'admin', defaultPriority: 40 },
    ],
  },

  // ── Equipo / Staff (admin-min; A4: module labelled "Equipo") ──
  {
    id: 'staff',
    label: 'Equipo',
    href: '/staff',
    icon: 'user-cog',
    minRole: 'admin',
    subPages: [
      { id: 'activos',    label: 'Activos',    href: '/staff/activos',    minRole: 'admin', defaultPriority: 100 },
      { id: 'asistencia', label: 'Asistencia', href: '/staff/asistencia', minRole: 'admin', defaultPriority: 70 },
      // A4: operational commissions (current quincena, per-provider
      // accrual) — admin. The superadmin history/margins view lives in
      // Reportes → "Comisiones · análisis".
      { id: 'comisiones', label: 'Comisiones', href: '/staff/comisiones', minRole: 'admin', defaultPriority: 40 },
    ],
  },

  // ── Verificaciones (interactive audit checklists, spec 2026-08-27) ──
  // Claude publishes checklists as rows; the whole team works through them.
  // Staff-min, no sub-pages — a flat list of reports.
  {
    id: 'verificaciones',
    label: 'Verificaciones',
    href: '/verificaciones',
    icon: 'clipboard-check', // check-square is Tareas' (iL30)
    minRole: 'staff',
    subPages: [],
  },

  // ── Pulso (superadmin-only) ──────────────────────────────────
  {
    id: 'pulso',
    label: 'Pulso',
    href: '/pulso',
    icon: 'activity',
    // Clinic-volume analytics (mensajes, citas, tickets) — superadmin, like
    // Reportes. proxy.ts gates /pulso; the API is requireRole(superadmin).
    minRole: 'superadmin',
    subPages: [
      { id: 'mensajes', label: 'Mensajes', href: '/pulso/mensajes', minRole: 'superadmin', defaultPriority: 100 },
      { id: 'citas',    label: 'Citas',    href: '/pulso/citas',    minRole: 'superadmin', defaultPriority: 80 },
      { id: 'tickets',  label: 'Tickets',  href: '/pulso/tickets',  minRole: 'superadmin', defaultPriority: 60 },
    ],
  },

  // ── Reportes (A3: module admin-min, per-sub-page gates) ───────
  {
    id: 'reportes',
    label: 'Reportes',
    href: '/reportes',
    icon: 'bar-chart',
    // Reportes is SUPERADMIN-only — enforced by proxy.ts (/reportes → superadmin)
    // and every /api/reportes/* route (requireRole superadmin). The rail gate
    // matches that truth so admins don't see a dead link they get bounced from.
    minRole: 'superadmin',
    mobileTabPriority: { superadmin: 65 }, // super 4th tab
    subPages: [
      { id: 'gastos',     label: 'Gastos',     href: '/reportes/gastos',     minRole: 'superadmin', defaultPriority: 10 },
      { id: 'inventario', label: 'Inventario', href: '/reportes/inventario', minRole: 'superadmin', defaultPriority: 8 },
      { id: 'staff',      label: 'Staff',      href: '/reportes/staff',      minRole: 'superadmin', defaultPriority: 6 },
      // Financial truth → superadmin. Ingresos is the superadmin landing.
      { id: 'ingresos',    label: 'Ingresos',            href: '/reportes/ingresos',    minRole: 'superadmin', defaultPriority: 100 },
      { id: 'comisiones',  label: 'Comisiones · análisis', href: '/reportes/comisiones',  minRole: 'superadmin', defaultPriority: 90 },
      { id: 'pacientes',   label: 'Pacientes',           href: '/reportes/pacientes',   minRole: 'superadmin', defaultPriority: 50 },
      { id: 'expedientes', label: 'Expedientes',         href: '/reportes/expedientes', minRole: 'superadmin', defaultPriority: 40 },
    ],
  },

  // ── Configuración (superadmin-only) ───────────────────────────
  {
    id: 'configuracion',
    label: 'Configuración',
    href: '/configuracion',
    icon: 'settings',
    minRole: 'superadmin',
    // The real sections (src/app/configuracion/*, labels from
    // CONFIGURACION_SECTIONS), so the phone/iPad menu lists them like IA or
    // Pulso. Every href is also in DashboardRail REAL_SUBROUTES. Facturación
    // stays out until it's built.
    subPages: [
      { id: 'clinica',       label: 'Clínica',               href: '/configuracion/clinica',       minRole: 'superadmin', defaultPriority: 100 },
      { id: 'miembros',      label: 'Miembros',              href: '/configuracion/miembros',      minRole: 'superadmin', defaultPriority: 90 },
      { id: 'integraciones', label: 'Integraciones',         href: '/configuracion/integraciones', minRole: 'superadmin', defaultPriority: 80 },
      { id: 'preferencias',  label: 'Listas y preferencias', href: '/configuracion/preferencias',  minRole: 'superadmin', defaultPriority: 70 },
      { id: 'seguridad',     label: 'Seguridad',             href: '/configuracion/seguridad',     minRole: 'superadmin', defaultPriority: 60 },
      { id: 'peligro',       label: 'Zona de peligro',       href: '/configuracion/peligro',       minRole: 'superadmin', defaultPriority: 10 },
    ],
  },

  // ── IA — the AI cockpit. Groups the assistant, the knowledge library that
  // grounds Comfy, the Comfy accuracy-review queue, and Comfy's spend page as
  // real sub-tabs under /ai/* (all in REAL_SUBROUTES, so the sub-row renders).
  // Last in the array so existing module indices are untouched (rail-config.test
  // pins inbox at index 1); display placement is driven by RAIL_GROUPS.
  // Per-sub-page gating: Asistente + Uso are superadmin, Biblioteca is all-staff,
  // Revisiones is reviewers (superadmin OR MD) via the requiresReviewer predicate.
  // The module shows whenever ≥1 sub-page is visible (every staff sees Biblioteca).
  {
    id: 'ai',
    label: 'IA',
    href: '/ai',
    icon: 'sparkles',
    minRole: 'staff',
    subPages: [
      { id: 'asistente',  label: 'Asistente',  href: '/ai/asistente',  minRole: 'superadmin', defaultPriority: 40 },
      { id: 'biblioteca', label: 'Biblioteca', href: '/ai/biblioteca', minRole: 'staff',      defaultPriority: 100 },
      { id: 'revisiones', label: 'Revisiones', href: '/ai/revisiones', minRole: 'staff', requiresReviewer: true, defaultPriority: 60 },
      { id: 'conversaciones', label: 'Conversaciones', href: '/ai/conversaciones', minRole: 'superadmin', defaultPriority: 30 },
      { id: 'instrucciones', label: 'Instrucciones', href: '/ai/instrucciones', minRole: 'superadmin', defaultPriority: 25 },
      { id: 'uso',        label: 'Uso',        href: '/ai/uso',        minRole: 'superadmin', defaultPriority: 20 },
      { id: 'autopilot',  label: 'Autopilot',  href: '/ai/autopilot',  minRole: 'superadmin', defaultPriority: 15 },
    ],
  },
];
